import {
  Box,
  Card,
  Grid,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  TextField as MuiTextField,
  Button,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useTranslate, useNotify, useRefresh } from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import ErrorPage403 from '../../../../error/403';
import {
  getOverlayStatus,
  setOverlayConfig,
  OverlayStatus,
  SetOverlayConfigInput,
  CriticalHitTier,
  getCriticalHitTiers,
  RedPacketType,
} from '../../../services/redpacketService';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

const OverlayList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [status, setStatus] = useState<OverlayStatus | null>(null);
  const [formData, setFormData] = useState<SetOverlayConfigInput>({
    enabled: false,
    removalTime: null,
    overlayType: null,
    reason: '',
  });
  const [removalTime, setRemovalTime] = useState<Dayjs | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setFetching(true);
      const response = await getOverlayStatus();
      setStatus(response);
      setFormData({
        enabled: response.enabled,
        removalTime: response.removalTime || null,
        overlayType: response.overlayType||null,
        reason: '',
      });
      if (response.removalTime) {
        setRemovalTime(dayjs(response.removalTime));
      } else {
        setRemovalTime(null);
      }
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.overlayFetchFailed'), { type: 'error' });
    } finally {
      setFetching(false);
    }
  }, [notify,translate]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      enabled: event.target.checked,
    }));
  };

  const handleRemovalTimeChange = (value: Dayjs | null) => {
    setRemovalTime(value);
    setFormData((prev) => ({
      ...prev,
      removalTime: value ? value.toISOString() : null,
    }));
  };

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      reason: event.target.value,
    }));
  };

  const handleOverlayTypeChange = (event: any) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      overlayType: value,
    }));
  };

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      const updateData: SetOverlayConfigInput = {
        enabled: formData.enabled,
        removalTime: formData.removalTime || null,
        overlayType: formData.overlayType || null,
        reason: formData.reason || undefined,
      };
      const response = await setOverlayConfig(updateData);
      setStatus(response);
      notify(translate('module.redpacket.overlayUpdateSuccess'), { type: 'success' });
      refresh();
      await fetchStatus();
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.overlayUpdateFailed'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [formData, notify, refresh, fetchStatus,translate]);

  return (
    <PermissionGuard resource="redpacket" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.redpacket.overlay')}
          links={[
            { name: translate('module.redpacket.redPacketManagement'), href: `/${Resources.redpacket}` },
            { name: translate('module.redpacket.overlay') },
          ]}
        />

        <Card sx={{ mt: 3, p: 3 }}>
          {status && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {translate('module.redpacket.currentStatus')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('module.redpacket.enabled')}
                  </Typography>
                  <Chip
                    label={status.enabled ? translate('module.redpacket.yes') : translate('module.redpacket.no')}
                    color={status.enabled ? 'success' : 'default'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('module.redpacket.currentlyShowing')}
                  </Typography>
                  <Chip
                    label={status.isCurrentlyShowing ? translate('module.redpacket.yes') : translate('module.redpacket.no')}
                    color={status.isCurrentlyShowing ? 'success' : 'default'}
                    size="small"
                  />
                </Grid>
                {status.overlayType && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      {translate('module.redpacket.overlayType')}
                    </Typography>
                    <Chip
                      label={status.overlayType}
                      color={status.overlayType === 'TIMED' ? 'primary' : 'default'}
                      size="small"
                    />
                  </Grid>
                )}
                {status.removalTime && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      {translate('module.redpacket.removalTime')}
                    </Typography>
                    <Typography variant="body1">
                      {new Date(status.removalTime).toLocaleString()}
                    </Typography>
                  </Grid>
                )}
                {status.countdownSeconds !== null && status.countdownSeconds !== undefined && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      {translate('module.redpacket.countdown')}
                    </Typography>
                    <Typography variant="body1">
                      {Math.floor(status.countdownSeconds / 3600)}h{' '}
                      {Math.floor((status.countdownSeconds % 3600) / 60)}m{' '}
                      {status.countdownSeconds % 60}s
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {translate('module.redpacket.configuration')}
            </Typography>
            <Stack spacing={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enabled}
                    onChange={handleEnabledChange}
                    disabled={loading}
                  />
                }
                label={translate('module.redpacket.enableOverlay')}
              />

              <FormControl fullWidth>
                <InputLabel>{translate('module.redpacket.overlayType')}</InputLabel>
                <Select
                  value={formData.overlayType || ''}
                  onChange={handleOverlayTypeChange}
                  label={translate('module.redpacket.overlayType')}
                  disabled={loading}
                >
                  <MenuItem value="TIMED">TIMED</MenuItem>
                  <MenuItem value="MANUAL">MANUAL</MenuItem>
                </Select>
              </FormControl>
              {formData?.overlayType === "TIMED"&&(
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label={translate('module.redpacket.removalTimeOptional')}
                    value={removalTime}
                    onChange={handleRemovalTimeChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: translate('module.redpacket.leaveEmptyIndefinite'),
                      },
                    }}
                    disabled={loading}
                  />
                </LocalizationProvider>
                )}

              <MuiTextField
                fullWidth
                label={translate('module.redpacket.reasonOptional')}
                multiline
                rows={3}
                value={formData.reason}
                onChange={handleReasonChange}
                placeholder={translate('module.redpacket.reasonForChange')}
                disabled={loading}
              />

              <PermissionButton resource="redpacket" action="update">
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading || fetching}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {loading ? translate('module.redpacket.saving') : translate('module.redpacket.saveConfiguration')}
                </Button>
              </PermissionButton>
            </Stack>
          </Box>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default OverlayList;
